import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import {
  UserPlus, LogIn, AlertTriangle, Receipt, FileText, Download, MessageCircle,
  BadgeCheck, MessageSquare, User, HelpCircle, ShieldCheck, QrCode, Sparkles, Key,
} from "lucide-react";
import axios from "axios";
import { useAuth } from "../../App";
import { toast } from "sonner";

/** Manual paso a paso para el Contribuyente.
 *  Reutilizable en el backoffice (tab "Contribuyente") y en el portal del contribuyente.
 */
const Step = ({ n, children }) => (
  <li className="flex gap-3 py-2">
    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="flex-1 text-slate-700 leading-relaxed">{children}</span>
  </li>
);
const Tip = ({ children }) => (
  <div className="mt-3 p-3 bg-orange-50 border border-orange-100 rounded-md flex gap-2 text-sm text-orange-900">
    <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
    <div>{children}</div>
  </div>
);

export default function ManualContribuyente({ inPortal = false }) {
  const { token, API } = useAuth();

  const descargarPdf = async () => {
    try {
      const r = await axios.get(`${API}/manuales/contribuyente/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "manual-contribuyente-sisaur.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.status === 403
        ? "Su rol no tiene permiso para descargar el manual."
        : "No se pudo descargar el PDF. Intente de nuevo.");
    }
  };

  return (
    <div className="space-y-6" data-testid="manual-contribuyente">
      {/* Hero */}
      <Card className="p-6 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50/40">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-green-700 text-white">
            <User className="w-7 h-7" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-display font-bold text-green-900">Manual del Contribuyente</h2>
            <p className="text-green-800/80 mt-1">
              Guía paso a paso para utilizar el <b>Portal del Contribuyente</b> de SISAUR (Palma Plus · Saneamiento Urbano):
              consulta tu deuda, reporta pagos, descarga planillas y solvencias, y comunícate con la alcaldía.
            </p>
            <p className="text-xs text-green-700/70 mt-2">
              {inPortal ? "Consúltalo cuando lo necesites desde «Ayuda / Manual» en el menú lateral." : "Este manual está disponible también para tus contribuyentes desde su portal."}
            </p>
          </div>
          <div className="flex-shrink-0">
            <Button
              onClick={descargarPdf}
              data-testid="btn-descargar-manual-pdf"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white font-medium shadow-md text-sm"
            >
              <Download className="w-4 h-4" /> Descargar PDF
            </Button>
            <p className="text-[10px] text-slate-500 mt-1 text-center">Guía ilustrada · 10 pág.</p>
          </div>
        </div>
      </Card>

      <Accordion type="multiple" defaultValue={["item-1"]} className="space-y-3" data-testid="manual-contribuyente-accordion">
        {/* 1. Registrarse */}
        <AccordionItem value="item-1" className="border border-green-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline" data-testid="manual-step-registro">
            <div className="flex items-center gap-3 text-left">
              <UserPlus className="w-5 h-5 text-green-700" />
              <div>
                <p className="font-semibold text-green-900">1. Registrarme en el portal (auto-registro desde la landing)</p>
                <p className="text-xs text-slate-500">4 escenarios: nuevo, pre-aprobado, rechazado o precargado desde Excel</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-4">
            <div className="text-sm text-slate-700 leading-relaxed">
              El registro es 100% online desde <a href="https://palmaplus.gob.ve" className="text-green-700 font-semibold underline">palmaplus.gob.ve</a>. Es un <b>asistente de 3 pasos</b> que valida en tiempo real tu RIF y correo. Según tu situación, el sistema te lleva por uno de estos 4 caminos:
            </div>

            <div className="border-l-4 border-green-500 pl-3 py-2 bg-green-50 rounded">
              <p className="font-bold text-green-900">🆕 A. Contribuyente NUEVO (nunca me registró la alcaldía)</p>
              <ol className="space-y-1 mt-2">
                <Step n={1}>Desde la landing, haz clic en <b>«¿Aún no tienes cuenta y tienes Licencia de Actividades Económicas? Regístrate como contribuyente aquí»</b>.</Step>
                <Step n={2}><b>Paso 1 — Identificación</b>: elige tipo de documento (V/E/J/G) y escribe el número. El sistema valida en vivo si el RIF ya existe.</Step>
                <Step n={3}><b>Paso 2 — Datos fiscales y contacto</b>: razón social, denominación comercial, actividad económica, área del establecimiento (m²), código catastral, cuenta contrato de electricidad, dirección fiscal, punto de referencia. Ubicación: estado, municipio, parroquia, sector.</Step>
                <Step n={4}><b>Paso 3 — Representante legal y credenciales</b>: nombre, cédula y celular del representante legal. Correo único (validado en vivo), contraseña ≥6 caracteres.</Step>
                <Step n={5}><b>Documentos adjuntos</b>: sube foto o PDF del RIF y del documento catastral (JPG/PNG/PDF).</Step>
                <Step n={6}>Marca la casilla de <b>Términos y Condiciones</b> (obligatorio). Podés leer el detalle desde el enlace.</Step>
                <Step n={7}>Presiona <b>«Finalizar registro»</b>. Recibirás pantalla de <b>Pre-aprobado</b>: un funcionario revisará tus datos en 24-48h.</Step>
              </ol>
            </div>

            <div className="border-l-4 border-amber-500 pl-3 py-2 bg-amber-50 rounded">
              <p className="font-bold text-amber-900">⏳ B. Ya me registré y estoy «Pre-aprobado»</p>
              <ol className="space-y-1 mt-2">
                <Step n={1}>Ingresa tu RIF en la landing. El sistema detecta que estás <b>Pre-aprobado</b>.</Step>
                <Step n={2}>Podés <b>iniciar sesión</b> (verás pantalla informando que tu cuenta está pendiente) o <b>actualizar tus datos</b> antes de la revisión.</Step>
                <Step n={3}>Para actualizar: te enviaremos un <b>código de verificación</b> a tu email/celular. Ingresa el código y modifica los campos que necesites.</Step>
              </ol>
            </div>

            <div className="border-l-4 border-red-500 pl-3 py-2 bg-red-50 rounded">
              <p className="font-bold text-red-900">❌ C. Mi registro fue «Rechazado» — quiero corregir</p>
              <ol className="space-y-1 mt-2">
                <Step n={1}>Ingresa tu RIF. El sistema muestra el <b>motivo del rechazo</b> escrito por el funcionario.</Step>
                <Step n={2}>Solicita el <b>código de verificación</b> (llega a correo o celular).</Step>
                <Step n={3}>Modifica los datos observados y re-envía. Tu registro vuelve a estado <b>Pre-aprobado</b> para nueva revisión.</Step>
              </ol>
            </div>

            <div className="border-l-4 border-sky-500 pl-3 py-2 bg-sky-50 rounded">
              <p className="font-bold text-sky-900">📄 D. Precargado desde Excel (la alcaldía me registró desde su sistema legado)</p>
              <ol className="space-y-1 mt-2">
                <Step n={1}>Si hiciste declaración en oficinas de Palma Plus, ya estás en el sistema pero <b>sin acceso al portal</b> (falta email y clave).</Step>
                <Step n={2}>Desde la landing, ingresa tu RIF. El sistema detecta que sos <b>precargado</b> y te ofrece completar los datos para activar el portal.</Step>
                <Step n={3}>Verifica tu identidad con el <b>código enviado a tu celular</b> registrado por la alcaldía (o email si lo tenés).</Step>
                <Step n={4}>Completa los campos faltantes: email, contraseña, documentos, T&C.</Step>
                <Step n={5}>Quedás <b>activo inmediatamente</b> (no requiere aprobación extra — tus datos ya fueron validados presencialmente).</Step>
              </ol>
            </div>

            <Tip>El sistema hace <b>validación en tiempo real</b>: si el RIF o email ya están registrados te avisa antes de continuar — así evitás duplicados. Además el asistente <b>guarda tu progreso</b>: si cerrás por accidente, podés retomar desde donde ibas.</Tip>
          </AccordionContent>
        </AccordionItem>

        {/* 2. Iniciar sesión */}
        <AccordionItem value="item-2" className="border border-green-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left">
              <LogIn className="w-5 h-5 text-green-700" />
              <div>
                <p className="font-semibold text-green-900">2. Iniciar sesión y recuperar contraseña</p>
                <p className="text-xs text-slate-500">Login con correo/contraseña + autoservicio de reset</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}>Desde la portada, haz clic en <b>«Soy Contribuyente»</b>.</Step>
              <Step n={2}>Ingresa tu <b>correo electrónico</b> y <b>contraseña</b>.</Step>
              <Step n={3}>Presiona <b>«Iniciar sesión»</b>. Verás tu dashboard con un resumen de tu situación.</Step>
            </ol>
            <div className="mt-3 p-3 bg-emerald-50 border border-emerald-100 rounded-md text-sm text-emerald-900">
              <p className="font-semibold mb-1 flex items-center gap-1"><Key className="w-4 h-4" /> ¿Olvidaste tu contraseña? — Autoservicio</p>
              <ol className="space-y-1 mt-1">
                <Step n={1}>En la pantalla de login, haz clic en <b>«¿Olvidaste tu contraseña?»</b>.</Step>
                <Step n={2}>Ingresa tu <b>correo o RIF</b>. Verás un mensaje: <i>«Si el dato existe, recibirás un enlace».</i></Step>
                <Step n={3}>Abre el correo que llegue (revisa la carpeta de spam si no aparece). Haz clic en el botón <b>«Restablecer contraseña»</b>.</Step>
                <Step n={4}>Escribe una nueva contraseña (≥6 caracteres). El enlace es válido por <b>24 horas</b> y solo puede usarse una vez.</Step>
                <Step n={5}>Vuelve al login y entra con la nueva contraseña.</Step>
              </ol>
            </div>
            <Tip>Si el enlace expiró o ya lo usaste, simplemente pide otro desde la misma opción — no hay límite de solicitudes. Si tampoco lo recibes, contactá a la alcaldía por WhatsApp.</Tip>
          </AccordionContent>
        </AccordionItem>

        {/* 3. Consultar deuda */}
        <AccordionItem value="item-3" className="border border-green-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <div>
                <p className="font-semibold text-green-900">3. Consultar mi deuda</p>
                <p className="text-xs text-slate-500">Revisa períodos pendientes y montos por pagar</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}>En el menú lateral, entra a <b>«Mi Deuda»</b>.</Step>
              <Step n={2}>Verás una lista de <b>planillas pendientes</b> con el período, N° de planilla, concepto y monto en Bolívares.</Step>
              <Step n={3}>En la parte superior aparece el <b>total adeudado</b> destacado en naranja.</Step>
              <Step n={4}>Puedes <b>descargar la planilla PDF</b> de cada período haciendo clic en el ícono de descarga.</Step>
            </ol>
            <Tip>Cada planilla muestra el desglose por mes y el <b>QR de pago</b> — súper útil si prefieres pagar directamente en oficina/banco con el código impreso.</Tip>
          </AccordionContent>
        </AccordionItem>

        {/* 4. Reportar pago */}
        <AccordionItem value="item-4" className="border border-green-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline" data-testid="manual-step-reportar-pago">
            <div className="flex items-center gap-3 text-left">
              <Receipt className="w-5 h-5 text-emerald-700" />
              <div>
                <p className="font-semibold text-green-900">4. Reportar un pago (Declarar)</p>
                <p className="text-xs text-slate-500">Sube tu comprobante bancario para validación</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="text-sm text-slate-600 mb-3">
              Después de realizar tu transferencia, Pago Móvil o depósito en las cuentas oficiales, <b>debes reportar tu pago</b> desde el portal para que la alcaldía lo valide.
            </div>
            <ol className="space-y-1">
              <Step n={1}>En el menú lateral, entra a <b>«Reportar Pago»</b>.</Step>
              <Step n={2}>Selecciona una o varias <b>planillas pendientes</b> a cubrir. El sistema sumará automáticamente los montos.</Step>
              <Step n={3}>Elige el <b>banco emisor</b>, el <b>banco receptor</b> (cuenta autorizada Palma Plus) y la <b>fecha del pago</b>.</Step>
              <Step n={4}>Ingresa la <b>referencia bancaria</b> (últimos 6 dígitos como mínimo) y el <b>monto</b> exacto pagado en Bs.</Step>
              <Step n={5}>Opcional: adjunta una imagen del comprobante o escribe una observación.</Step>
              <Step n={6}>Presiona <b>«Enviar reporte»</b>. Verás un mensaje: <i>«Pago en revisión por la alcaldía»</i>.</Step>
            </ol>
            <Tip>El sistema tiene <b>conciliación automática</b>: si sube el estado de cuenta del banco y coinciden referencia + monto + fecha, tu pago se valida automáticamente en segundos. En otros casos, un funcionario lo revisa manualmente.</Tip>
          </AccordionContent>
        </AccordionItem>

        {/* 5. Ver y descargar planillas */}
        <AccordionItem value="item-5" className="border border-green-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left">
              <FileText className="w-5 h-5 text-green-700" />
              <div>
                <p className="font-semibold text-green-900">5. Ver y descargar planillas de cobro</p>
                <p className="text-xs text-slate-500">PDF oficial con QR de pago y equivalencia USD/EUR</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}>Entra a <b>«Mi Deuda»</b> o <b>«Historial de Pagos»</b>.</Step>
              <Step n={2}>Junto a cada planilla verás el ícono <Download className="inline w-4 h-4 text-green-700" /> <b>Descargar</b>.</Step>
              <Step n={3}>Se descargará un PDF con: cabecera institucional, tus datos, detalle de impuestos y tasas, subtotal, otros conceptos (multas/descuentos si aplican) y el <b>Total a pagar</b> en Bs. con equivalencia en USD y EUR según la cotización oficial.</Step>
              <Step n={4}>Al pie encontrarás un <b>código QR</b> con el enlace directo a la pantalla de pago del portal.</Step>
            </ol>
            <Tip>También podés enviarte la planilla por WhatsApp: usa el botón <MessageCircle className="inline w-4 h-4 text-emerald-700" /> junto a cada planilla — se abre WhatsApp con un mensaje pre-cargado y el link al PDF.</Tip>
          </AccordionContent>
        </AccordionItem>

        {/* 6. Comprobantes de pago */}
        <AccordionItem value="item-6" className="border border-green-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left">
              <QrCode className="w-5 h-5 text-green-700" />
              <div>
                <p className="font-semibold text-green-900">6. Descargar mis comprobantes de pago</p>
                <p className="text-xs text-slate-500">Recibos oficiales con QR de verificación</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}>Entra a <b>«Historial de Pagos»</b> en el menú lateral.</Step>
              <Step n={2}>Verás la lista de pagos que reportaste con su estado: <b>Pendiente de validación</b>, <b>Validado</b> o <b>Rechazado</b>.</Step>
              <Step n={3}>Para los pagos <b>Validados</b>, aparece el ícono <Download className="inline w-4 h-4 text-green-700" /> — descarga el <b>recibo oficial en PDF</b> con N° de comprobante y firma electrónica.</Step>
              <Step n={4}>El recibo incluye un <b>QR de verificación</b>: cualquier persona puede escanearlo con la cámara del móvil y ver la validez del comprobante en la web pública de SISAUR (sin necesidad de iniciar sesión).</Step>
            </ol>
            <Tip>Guarda tus comprobantes: son <b>documentos oficiales</b> con validez legal. Si necesitas presentarlos a un tercero (banco, comercio, junta comunal), el QR le permite verificarlos al instante.</Tip>
          </AccordionContent>
        </AccordionItem>

        {/* 7. Solvencia */}
        <AccordionItem value="item-7" className="border border-green-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left">
              <BadgeCheck className="w-5 h-5 text-emerald-700" />
              <div>
                <p className="font-semibold text-green-900">7. Mi Solvencia de Aseo Urbano</p>
                <p className="text-xs text-slate-500">Certificado oficial de estar al día con el impuesto</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}>En el menú lateral, entra a <b>«Mi Solvencia»</b>.</Step>
              <Step n={2}>Verás un banner grande: <span className="inline-block px-2 py-0.5 rounded bg-green-600 text-white text-xs">SOLVENTE ✓</span> (verde) si tu último pago cubre el mes en curso, o <span className="inline-block px-2 py-0.5 rounded bg-red-600 text-white text-xs">NO SOLVENTE</span> (rojo) si tenés pendientes.</Step>
              <Step n={3}>Si estás solvente, aparece el botón <b>«Emitir solvencia»</b>. Al pulsarlo se genera un <b>certificado oficial PDF</b> con correlativo (ej: <code className="text-xs bg-slate-100 px-1 rounded">SOLV-00007/2026</code>), vigente hasta fin de mes.</Step>
              <Step n={4}>Podés descargarlo, imprimirlo o compartirlo por WhatsApp desde el mismo botón.</Step>
              <Step n={5}>El certificado incluye un <b>QR</b>: cualquier organismo (banco, comercio, alcaldía) puede escanearlo para <b>verificar autenticidad</b> desde la web pública sin necesidad de contactarnos.</Step>
            </ol>
            <Tip>La solvencia es <b>idempotente</b>: si ya emitiste una este mes y todavía es vigente, el sistema te devuelve la misma en vez de generar otra nueva. Válida hasta el último día del mes de emisión.</Tip>
          </AccordionContent>
        </AccordionItem>

        {/* 8. Reclamos */}
        <AccordionItem value="item-8" className="border border-green-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-semibold text-green-900">8. Abrir y hacer seguimiento de reclamos</p>
                <p className="text-xs text-slate-500">Reporta problemas con el servicio, tu factura u otros</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ol className="space-y-1">
              <Step n={1}>Ve a <b>«Mis Reclamos»</b> en el menú lateral.</Step>
              <Step n={2}>Pulsa <b>«Nuevo reclamo»</b>. Elige el tipo: <i>Problema con recolección</i>, <i>Factura incorrecta</i> u <i>Otros</i>.</Step>
              <Step n={3}>Describe el problema con el mayor detalle posible (fecha, dirección, qué pasó).</Step>
              <Step n={4}>Envía. Recibirás un número de ticket y podrás seguir el estado: <b>abierto</b> → <b>en revisión</b> → <b>en proceso</b> → <b>resuelto</b>.</Step>
              <Step n={5}>Si el reclamo es <b>rechazado</b>, verás el motivo. Podés reabrirlo si aportas más información.</Step>
              <Step n={6}>Un reclamo resuelto se <b>cierra automáticamente</b> a los 7 días si no lo reabres.</Step>
            </ol>
            <Tip>Cada cambio de estado queda registrado con fecha, hora y funcionario responsable — vas a poder ver la línea de tiempo completa del reclamo.</Tip>
          </AccordionContent>
        </AccordionItem>

        {/* 9. Actualizar datos */}
        <AccordionItem value="item-9" className="border border-green-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left">
              <User className="w-5 h-5 text-green-700" />
              <div>
                <p className="font-semibold text-green-900">9. Actualizar mis datos</p>
                <p className="text-xs text-slate-500">Autoservicio, perfil o vía reclamo según el dato</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <p className="text-sm text-slate-600 mb-2">
              Podés actualizar tus datos de <b>tres formas</b> según qué necesitas modificar:
            </p>
            <div className="border-l-4 border-green-500 pl-3 py-2 bg-green-50 rounded mb-2">
              <p className="font-bold text-green-900">✅ Autoservicio desde la landing (si aún no fuiste aprobado)</p>
              <ol className="space-y-1 mt-2">
                <Step n={1}>Ingresa tu RIF en la landing. Si estás en estado <b>Pre-aprobado</b> o <b>Rechazado</b>, aparece el botón <b>«Actualizar mis datos»</b>.</Step>
                <Step n={2}>Solicita el <b>código de verificación</b> a tu email o celular registrado.</Step>
                <Step n={3}>Ingresa el código y modifica los campos que necesites. Los cambios entran directamente a revisión del funcionario.</Step>
              </ol>
            </div>
            <div className="border-l-4 border-sky-500 pl-3 py-2 bg-sky-50 rounded mb-2">
              <p className="font-bold text-sky-900">📞 Actualizar teléfono o correo de contacto (ya activo en el portal)</p>
              <ol className="space-y-1 mt-2">
                <Step n={1}>Desde el portal, abre <b>«Mi Perfil»</b>. Podés actualizar celular y correo de contacto directamente.</Step>
                <Step n={2}>Guarda. Si cambiaste el correo, verifica el enlace que llegará al nuevo email.</Step>
              </ol>
            </div>
            <div className="border-l-4 border-amber-500 pl-3 py-2 bg-amber-50 rounded">
              <p className="font-bold text-amber-900">📝 Cambios sensibles: nombre, RIF, dirección fiscal, actividad</p>
              <ol className="space-y-1 mt-2">
                <Step n={1}>Estos datos <b>solo puede modificarlos un funcionario</b> por seguridad fiscal.</Step>
                <Step n={2}>Abre un reclamo tipo <b>«Otros»</b> desde <b>«Mis Reclamos»</b> indicando qué necesitas cambiar y aportando la documentación de respaldo (SENIAT, actualización de LAE, etc.).</Step>
                <Step n={3}>Un funcionario procesará el cambio en 24-48 horas y verás los datos actualizados en tu próximo login.</Step>
              </ol>
            </div>
            <Tip>Mantener el teléfono actualizado es clave — usamos WhatsApp para confirmarte pagos validados y recordarte vencimientos.</Tip>
          </AccordionContent>
        </AccordionItem>

        {/* 10. Seguridad */}
        <AccordionItem value="item-10" className="border border-green-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left">
              <ShieldCheck className="w-5 h-5 text-green-700" />
              <div>
                <p className="font-semibold text-green-900">10. Seguridad y buenas prácticas</p>
                <p className="text-xs text-slate-500">Cuida tu cuenta y tus documentos</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ul className="list-disc pl-6 text-sm text-slate-700 space-y-1">
              <li>No compartas tu contraseña con nadie. La alcaldía nunca te la va a pedir.</li>
              <li>Descarga tus comprobantes y solvencias en el momento — tu equipo local es tu mejor respaldo.</li>
              <li>Verifica siempre la URL del portal antes de ingresar tu contraseña.</li>
              <li>Si detectas actividad sospechosa (pagos que no reconoces), abre un reclamo urgente.</li>
              <li>Cierra sesión desde un dispositivo compartido cuando termines.</li>
            </ul>
          </AccordionContent>
        </AccordionItem>

        {/* 11. Contacto */}
        <AccordionItem value="item-11" className="border border-green-100 rounded-lg bg-white">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3 text-left">
              <HelpCircle className="w-5 h-5 text-orange-600" />
              <div>
                <p className="font-semibold text-green-900">11. ¿Necesitas ayuda? Contactos</p>
                <p className="text-xs text-slate-500">Canales oficiales de atención</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="grid md:grid-cols-3 gap-3 text-sm">
              <Card className="p-3 border-green-100">
                <p className="font-semibold text-green-900 flex items-center gap-1"><MessageCircle className="w-4 h-4" /> WhatsApp</p>
                <p className="text-slate-600 mt-1">0414-2404616 — Consultas rápidas</p>
              </Card>
              <Card className="p-3 border-green-100">
                <p className="font-semibold text-green-900">🏢 Oficina Palma Center</p>
                <p className="text-slate-600 mt-1">La Victoria, Edo. Aragua — Horario oficial de atención</p>
              </Card>
              <Card className="p-3 border-green-100">
                <p className="font-semibold text-green-900">🧾 Oficina de Hacienda</p>
                <p className="text-slate-600 mt-1">Taquilla Palma Plus en la alcaldía</p>
              </Card>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Card className="p-4 border-green-100 bg-green-50/40 text-center">
        <p className="text-sm text-green-900">
          <b>Palma Plus, Saneamiento Urbano</b> · <span className="text-green-700/70">SISAUR - Sistema de Saneamiento Urbano</span>
        </p>
        <p className="text-xs text-green-700/60 mt-1">«Por una ciudad más limpia» · Sumamos limpieza, sumamos bienestar para todos 🌱</p>
      </Card>
    </div>
  );
}
